import { useCallback, useState } from 'react';

export type HelpFeedbackValue = 'up' | 'down';

export interface HelpMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  langfuseTraceId?: string;
  langfuseObservationId?: string | null;
  feedbackValue?: HelpFeedbackValue;
  feedbackState?: 'idle' | 'submitting' | 'submitted' | 'error';
  feedbackSubmittedAt?: Date;
}

export interface UseHelpChatReturn {
  messages: HelpMessage[];
  isStreaming: boolean;
  sendMessage: (text: string) => Promise<void>;
  submitFeedback: (messageId: string, value: HelpFeedbackValue) => Promise<void>;
  clearMessages: () => void;
}

interface HelpStreamEvent {
  type: 'token' | 'done' | 'error';
  content?: string;
  message?: string;
  metadata?: {
    trace_id?: string;
    observation_id?: string | null;
  };
}

const LANGFUSE_SCORE_API = '/api/dotnet/langfuse/scores';

function makeMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return Math.random().toString(36).slice(2, 10);
}

export function useHelpChat(
  pageContext?: string,
  getToken?: () => Promise<string | null>,
): UseHelpChatReturn {
  const [messages, setMessages] = useState<HelpMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);

  const sendMessage = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isStreaming) return;

    const userMsg: HelpMessage = { id: makeMessageId(), role: 'user', content: trimmed };
    const assistantMessageId = makeMessageId();
    const history = [...messages, userMsg];

    setMessages([...history, { id: assistantMessageId, role: 'assistant', content: '' }]);
    setIsStreaming(true);

    try {
      const token = getToken ? await getToken() : null;
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch('/api/python/help/chat', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message: trimmed,
          history: messages.map((message) => ({ role: message.role, content: message.content })),
          ...(pageContext ? { page_context: pageContext } : {}),
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assistantText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;

          const event = JSON.parse(line.slice(6)) as HelpStreamEvent;

          if (event.type === 'token' && event.content) {
            assistantText += event.content;
            setMessages((current) => current.map((message) =>
              message.id === assistantMessageId
                ? { ...message, content: assistantText }
                : message,
            ));
          }

          if (event.type === 'done') {
            setMessages((current) => current.map((message) =>
              message.id === assistantMessageId
                ? {
                    ...message,
                    langfuseTraceId: event.metadata?.trace_id,
                    langfuseObservationId: event.metadata?.observation_id,
                  }
                : message,
            ));
          }

          if (event.type === 'error') {
            throw new Error(event.message || 'stream-error');
          }
        }
      }
    } catch {
      setMessages((current) => current.map((message) =>
        message.id === assistantMessageId
          ? { ...message, content: 'Fehler beim Laden der Antwort. Bitte erneut versuchen.' }
          : message,
      ));
    } finally {
      setIsStreaming(false);
    }
  }, [getToken, messages, isStreaming, pageContext]);

  const submitFeedback = useCallback(async (messageId: string, value: HelpFeedbackValue) => {
    const targetMessage = messages.find((message) => message.id === messageId && message.langfuseTraceId);
    if (!targetMessage || targetMessage.feedbackState === 'submitting' || targetMessage.feedbackState === 'submitted') {
      return;
    }

    setMessages((current) => current.map((message) =>
      message.id === messageId
        ? { ...message, feedbackValue: value, feedbackState: 'submitting' }
        : message,
    ));

    try {
      const token = getToken ? await getToken() : null;
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(LANGFUSE_SCORE_API, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          traceId: targetMessage.langfuseTraceId,
          observationId: targetMessage.langfuseObservationId ?? null,
          value: value === 'up',
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const submittedAt = new Date();
      setMessages((current) => current.map((message) =>
        message.id === messageId
          ? { ...message, feedbackValue: value, feedbackState: 'submitted', feedbackSubmittedAt: submittedAt }
          : message,
      ));
    } catch {
      setMessages((current) => current.map((message) =>
        message.id === messageId
          ? { ...message, feedbackValue: value, feedbackState: 'error' }
          : message,
      ));
    }
  }, [getToken, messages]);

  const clearMessages = useCallback(() => setMessages([]), []);

  return { messages, isStreaming, sendMessage, submitFeedback, clearMessages };
}
